export type NavLink = {
  id: string;
  href: string;
  label: string;
  badge?: string;
};

export const NAV_VISIBILITY_CLASSES = {
  desktop: "hidden min-[1120px]:flex",
  mobileToggle: "flex min-[1120px]:hidden",
  mobilePanel: "min-[1120px]:hidden",
} as const;

export const PUBLIC_PRIMARY_NAV_LINKS: NavLink[] = [
  { id: "jobs", href: "/jobs", label: "Jobs" },
  { id: "browse", href: "/browse", label: "Candidates" },
  { id: "conditions", href: "/conditions", label: "Conditions" },
  { id: "companion", href: "/companion", label: "Companion" },
];

export const CANDIDATE_PRIMARY_NAV_LINKS: NavLink[] =
  PUBLIC_PRIMARY_NAV_LINKS.filter((link) => link.id !== "browse");

export function primaryNavLinksFor(
  role: "candidate" | "employer" | null | undefined,
): NavLink[] {
  return role === "candidate"
    ? CANDIDATE_PRIMARY_NAV_LINKS
    : PUBLIC_PRIMARY_NAV_LINKS;
}

export const SECONDARY_NAV_LINKS: NavLink[] = [
  { id: "employers", href: "/employers", label: "Employers" },
  { id: "plan", href: "/plan", label: "Plan" },
  { id: "research", href: "/research", label: "Research" },
  { id: "community", href: "/community", label: "Community" },
  { id: "festivals", href: "/festivals", label: "Festivals" },
  { id: "museum", href: "/museum", label: "Museum", badge: "Beta" },
];

export const RESOURCE_NAV_LINKS: NavLink[] = [
  { id: "guides", href: "/guides", label: "Guides" },
];

export const ABOUT_NAV_LINKS: NavLink[] = [
  { id: "about", href: "/about", label: "Our Story" },
  { id: "faq", href: "/faq", label: "FAQ" },
  { id: "refer", href: "/refer", label: "Ambassadors" },
  { id: "partnerships", href: "/partnerships", label: "Brand Partnerships" },
  { id: "feedback", href: "/feedback", label: "Feedback" },
];

export const ABOUT_PEAKWAVE_NAV_LINKS: NavLink[] = [
  { id: "how-it-works", href: "/how-it-works", label: "How it works" },
  { id: "for-seasonaires", href: "/for-seasonaires", label: "For seasonaires" },
  { id: "for-employers", href: "/for-employers", label: "For employers" },
  { id: "colin", href: "/colin", label: "Meet Colin" },
];

export const MORE_NAV_LINKS: NavLink[] = [
  ...SECONDARY_NAV_LINKS,
  ...RESOURCE_NAV_LINKS,
  ...ABOUT_NAV_LINKS,
  ...ABOUT_PEAKWAVE_NAV_LINKS,
];

export const EXPLORE_NAV_LINKS: NavLink[] = [
  PUBLIC_PRIMARY_NAV_LINKS.find((link) => link.id === "companion"),
  PUBLIC_PRIMARY_NAV_LINKS.find((link) => link.id === "conditions"),
].filter((link): link is NavLink => Boolean(link));

export const CANDIDATE_ACCOUNT_LINKS: NavLink[] = [
  { id: "profile", href: "/profile", label: "Profile" },
  { id: "settings", href: "/settings", label: "Settings" },
];

export function candidateAccountLinks(input: {
  profileId?: string;
  workProfileVisible: boolean;
}): NavLink[] {
  if (!input.profileId || !input.workProfileVisible) {
    return CANDIDATE_ACCOUNT_LINKS;
  }

  return [
    ...CANDIDATE_ACCOUNT_LINKS,
    {
      id: "public-profile",
      href: `/candidates/${input.profileId}`,
      label: "View work profile",
    },
  ];
}

export function companionAccountBadgeLabels(input: {
  callsUnread: boolean;
  hasEpicSpot: boolean;
}): ("Call" | "Conditions")[] {
  const labels: ("Call" | "Conditions")[] = [];
  if (input.callsUnread) labels.push("Call");
  if (input.hasEpicSpot) labels.push("Conditions");
  return labels;
}

const ACTIVE_ROUTE_ALIASES: Record<string, string[]> = {
  "/companion": ["/my-ski", "/my-surf"],
};

export function isNavLinkActive(pathname: string, href: string): boolean {
  if (pathname === href || pathname.startsWith(`${href}/`)) return true;
  return (ACTIVE_ROUTE_ALIASES[href] ?? []).some(
    (alias) => pathname === alias || pathname.startsWith(`${alias}/`),
  );
}
